import { fileSchema, linkSchema } from "@/src/lib/schemas";
import { z } from "zod";

const locationRefSchema = z.object({ id: z.string().uuid(), names: z.string() });

const editableMetaSchema = z.object({
  checkTimes: z.object({ checkIn: z.string(), checkOut: z.string() }),
  keyFeatures: z.array(z.string()),
  brochureLink: z.string(),
  videoUrl: z.string(),
  planUrl: z.string(),
  summerPorts: z.string(),
  winterPorts: z.string(),
  summerAreas: z.string(),
  winterAreas: z.string(),
  accomodations: z.string(),
  otherToys: z.string(),
  brokersNote: z.object({
    brokerId: z.string(),
    text: z.string(),
  }),
  sampleMenu: z.object({
    description: z.string(),
    images: z.array(z.string()),
  }),
  crewInfo: z.object({
    photo: z.string(),
    count: z.number().int().min(0).nullable(),
    description: z.string(),
    languages: z.string(),
    members: z.array(
      z.object({
        img: z.string(),
        name: z.string(),
        title: z.string(),
        nationality: z.string(),
        bio: z.string(),
      }),
    ),
  }),
  perGuestRates: z.array(
    z.object({
      seasonName: z.string(),
      terms: z.string(),
      mcaCompliance: z.string(),
      items: z.array(
        z.object({
          guests: z.number().int().min(0),
          rate: z.string(),
        }),
      ),
    }),
  ),
});

export const YachtForm = z.object({
  meta: editableMetaSchema,
  basicDetails: z.object({
    photos: z.array(fileSchema).min(1),
    name: z.string().min(1),
    labels: z
      .array(z.object({ id: z.string().uuid(), value: z.string() }))
      .max(2)
      .optional(),
    model: linkSchema,
    provider: z.enum(["NS", "CYA", "BM", "Helm", "Yachtfolio", "Ankor"]).default("Helm"),
    providerId: z.string().optional().nullable(),
    manufacturer: z.string(),
    productionYear: z.number().int().min(1800).max(new Date().getFullYear()),
    startDay: z.enum(["1", "2", "3", "4", "5", "6", "0", "any day"]).optional(),
    minimumCharterDuration: z.number().int().min(1).or(z.string()).optional(),
    type: z.enum([
      "Sailing yacht",
      "Catamaran",
      "Do not display",
      "Gulet",
      "Jet ski",
      "Power catamaran",
      "R.I.B / Speed boat",
      "Other",
      "Motor yacht",
    ]),
    operator: linkSchema,
    // TODO: sample menu, guest comments, crew info
    sampleMenu: z
      .object({
        pictures: z.array(fileSchema),
        description: z.string(),
      })
      .optional(),
    guestComments: z
      .array(
        z.object({
          dateOrSeason: z.string(),
          comment: z.string(),
          client: z.string(),
        }),
      )
      .optional(),
    crewInfo: z.object({}).optional(),
    charterTypes: z
      .object({
        skippered: z.boolean(),
        crewed: z.boolean(),
        bareboat: z.boolean(),
      })
      .refine((data) => Object.values(data).includes(true), {
        message: "At least one charter type must be true",
        path: ["charterTypes"],
      }),
    offerCreatorVisibility: z.enum(["visible", "hidden", "auto"]),
    helmVisibility: z.enum(["visible", "hidden", "auto"]),
    anchorVisibility: z.enum(["visible", "hidden", "auto"]),
    description: z.string().optional(),
    notes: z.string().optional(),
    ratingAnchor: z.number().int().min(1).max(10).nullable(),
    ratingHelm: z.number().int().min(1).max(10).nullable(),
  }),
  locationAndPricing: z.object({
    securityDeposit: z.object({
      price: z.number().int().min(0),
      currency: z.string(),
    }),
    pricing: z.discriminatedUnion("type", [
      z.object({
        type: z.literal("simple"),
        location: locationRefSchema,
        price: z.number().int().min(0),
        currency: z.string(),
        per: z.enum(["day", "week", "charter", "hour"]),
        vat: z.number().min(0),
        apa: z.number().min(0),
      }),
      z.object({
        type: z.literal("seasonal"),
        prices: z.array(
          z.object({
            locations: z.array(locationRefSchema).nonempty(),
            price: z.number().int().min(0),
            currency: z.string(),
            per: z.enum(["day", "week", "charter", "hour"]),
            vat: z.number().min(0),
            apa: z.number().min(0),
            dateRange: z.object({
              startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
              endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
            }),
          }),
        ),
      }),
    ]),
  }),
  specification: z.object({
    length: z.number().min(0),
    beam: z.number().min(0),
    mainsailType: z.enum(["racing", "cruising", "not_available"]),
    cabins: z.number().int().min(0),
    crewCabins: z.number().int().min(0),
    berths: z.number().int().min(0),
    heads: z.number().int().min(0),
    maxPeople: z.number().int().min(0),
    crewNumber: z.number().int().min(0),
    extraSpecs: z.array(z.object({ name: z.string().min(1), value: z.string().min(1) })),
  }),
  amenities: z.array(
    z.object({
      id: z.string(),
      name: z.string().min(1, { message: "Field required" }),
      obligatory: z.boolean(),
      formula: z.string(),
      type: z.string().optional(),
      paid_locally: z.boolean().optional(),
      included: z.boolean().optional(),
      provider: z.enum(["Helm", "CYA", "NS", "BM", "Yachtfolio", "Ankor"]).optional(),
      price: z
        .object({
          price: z.number(),
          per: z.enum(["day", "week", "charter", "hour"]).optional(),
          currency: z.string().optional(),
          where: z.string(),
        })
        .nullable(),
      currency: z.object({
        code: z.string().nullable(),
      }),
    }),
  ),
});

export type YachtForm = z.infer<typeof YachtForm>;
export type YachtLocationRef = z.infer<typeof locationRefSchema>;

export type YachtAmenity = YachtForm["amenities"][number];

export const emptyYachtMeta: YachtForm["meta"] = {
  checkTimes: { checkIn: "", checkOut: "" },
  keyFeatures: [],
  brochureLink: "",
  videoUrl: "",
  planUrl: "",
  summerPorts: "",
  winterPorts: "",
  summerAreas: "",
  winterAreas: "",
  accomodations: "",
  otherToys: "",
  brokersNote: { brokerId: "", text: "" },
  sampleMenu: { description: "", images: [] },
  crewInfo: {
    photo: "",
    count: null,
    description: "",
    languages: "",
    members: [],
  },
  perGuestRates: [],
};

export const emptyYachtForm: YachtForm = {
  meta: emptyYachtMeta,
  basicDetails: {
    photos: [],
    name: "",
    labels: [],
    model: { id: "", name: "" },
    provider: "Helm",
    providerId: "",
    manufacturer: "",
    productionYear: 1900,
    startDay: "6",
    minimumCharterDuration: 1,
    charterTypes: {
      skippered: false,
      crewed: false,
      bareboat: false,
    },
    type: "Sailing yacht",
    operator: { id: "", name: "" },
    offerCreatorVisibility: "auto",
    helmVisibility: "auto",
    anchorVisibility: "auto",
    description: "",
    notes: "",
    ratingAnchor: null,
    ratingHelm: null,
  },
  locationAndPricing: {
    securityDeposit: {
      price: 0,
      currency: "EUR",
    },
    pricing: {
      type: "simple",
      location: undefined as any,
      price: 0,
      currency: "EUR",
      per: "day",
      vat: 0,
      apa: 0,
    },
  },
  specification: {
    length: 0,
    beam: 0,
    mainsailType: "cruising",
    cabins: 0,
    berths: 0,
    heads: 0,
    maxPeople: 0,
    crewCabins: 0,
    crewNumber: 0,
    extraSpecs: [],
  },
  amenities: [],
};
export const emptyYachtAmenity: YachtAmenity = {
  id: "",
  name: "",
  obligatory: false,
  formula: "",
  included: false,
  provider: "Helm",
  price: {
    price: 0,
    per: "day",
    currency: "EUR",
    where: "in_advance",
  },
  currency: {
    code: "",
  },
};

function toIsoDateTime(value: string, endOfDay = false) {
  if (value.includes("T")) {
    return value;
  }
  return `${value}T${endOfDay ? "23:59:59.999" : "00:00:00.000"}Z`;
}

export function serializeYachtForm(form: YachtForm) {
  return {
    ...form,
    meta: form.meta,
    locationAndPricing: {
      ...form.locationAndPricing,
      pricing:
        form.locationAndPricing.pricing.type === "simple"
          ? {
              ...form.locationAndPricing.pricing,
              vat: form.locationAndPricing.pricing.vat,
              apa: form.locationAndPricing.pricing.apa,
              location: {
                id: form.locationAndPricing.pricing.location.id,
              },
            }
          : {
              ...form.locationAndPricing.pricing,
              prices: form.locationAndPricing.pricing.prices.map((price) => ({
                ...price,
                vat: price.vat,
                apa: price.apa,
                locations: price.locations.map((location) => location.id),
                dateRange: {
                  startDate: toIsoDateTime(price.dateRange.startDate),
                  endDate: toIsoDateTime(price.dateRange.endDate, true),
                },
              })),
            },
    },
  };
}
